import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {Tabs, Layout, Divider} from "antd";
import Button from '@mui/material/Button';
import { useLocation } from 'react-router-dom'; // Import useLocation
import SmaDrafts from "./SmaDrafts";
import Alert from '@mui/material/Alert';
import TickerSearch from '../../modules/Search/TickerSearch/TickerSearch';
import PageHeader from "../../layouts/PageHeader";
import Placeholder from "../../components/Placeholder";
import {iconMapping} from "../../configs/AppsConfig";
import useSma from './useSma';
import SmaTemplates from "./SmaTemplates";
import SectorComparisonBarChart from "./Sectors/SectorComparisonBarChart";
import EtfCircleCharts from "./EtfCircleCharts";
import CumulativeHoldingsTable from "./CumulativeHoldingsTable";
import formatCumulativeEtfHoldings from "./utils/formatCumulativeEtfHoldings";
import SectorTracking from "./Sectors/SectorTracking";
import {calculateEtfContributions} from "./utils/smaUtils";
import SmaConfigurator from "./SmaConfigurator";
import SmaPreview from "./SmaPreview/SmaPreview";
import StackedCubesChart from "../../components/ThreeDimensional/CubeGrid";
import {useTickerSearchIndex} from "../../modules/Search/useTickerSearchIndex";
import { initializeNewSmaConfig } from "../../store/slices/sma.slice";

const Sma = () => {
    const location = useLocation(); // Use the useLocation hook to get the current location
    const [view, setView] = useState('templates');
    const isPublic = useSelector(state=>state.public.isPublicAccess)
    const dispatch = useDispatch();

    const currentSmaConfigId = useSelector(state => state.sma.currentSmaConfigId);

    // Add this useEffect to handle SMA config changes
    useEffect(() => {
        if (currentSmaConfigId) {
            setView('table');
        }
    }, [currentSmaConfigId]);

    const {
        addEtfs, removeEtf, etfColors, etfData,
        sectorColors, mergedData, filledHoldings, summaryDetails,
        currentSmaConfig, updateConfig, updateEtfWeight, toggleReplicateEtf,
        blacklists, screens,
        sectorsBarData, sectorsBarChartField, setSectorsBarChartField,
        saveSma, getSectorTickers, allSectors,
        replicateKey,
        sectorRemainders, sectorWeightChanges
    } = useSma();



    const etfContributions = etfData && filledHoldings && calculateEtfContributions(filledHoldings, etfData);


    const onAddEtfs = (etfs)=>{
        addEtfs(etfs);
        setView('table');
    }

    const getReplicatedEtfsData = ()=>{
        if (Object.keys(combinedEtfData).length === 0) return null;
        return Object.keys(combinedEtfData).filter(x=>!combinedEtfData[x].doNotReplicate).map(x=>({symbol: x, ...combinedEtfData[x]}));
    }


    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const viewParam = queryParams.get('view');
        if (viewParam) {
            setView(viewParam);
        }
    }, [location.search]); // Re-run the effect if the search string changes


    const combinedEtfData = {};
    Object.keys(etfData).forEach((key) => {
      combinedEtfData[key] = { ...etfData[key], ...currentSmaConfig?.etfs?.[key] };
    });
    const dataReady = Object.values(etfData).some(x=>x!==null);
    const replicatedEtfs = dataReady && getReplicatedEtfsData();
    let cumulativeTableRows;
    try {
        cumulativeTableRows = dataReady && formatCumulativeEtfHoldings(replicatedEtfs);
    }
    catch(e){}
    const uniqueTickers = filledHoldings?.map?.(x=>x.symbol);
    const { tickers } = useTickerSearchIndex(uniqueTickers||[]);

    const handleNewSma = ()=>{
        dispatch(initializeNewSmaConfig());
        setView('table');
    }

    // if (!(Object.values(etfData).some(x=>x!==null))) {
    //     return (
    //         <div>empty</div>
    //     )
    // }

    return (
        <Layout className={'max-h-full'}>
            {view !== 'preview' && (
                <PageHeader title={'SMA Builder'} logoUrl={iconMapping.smalab}
                            actions={[
                                <Button onClick={handleNewSma}>Create New</Button>,
                                view==='search' ? <Button color={'error'} variant={'outlined'} onClick={()=>setView('table')}>Cancel Search</Button> : <Button onClick={()=>setView('search')}>Search ETFs</Button> ,
                                <Button onClick={()=>setView('templates')}>View Templates</Button>,
                                <Button onClick={()=>setView('drafts')}>View Drafts</Button>
                            ]}
                />
            )}
            {view === 'preview' && Array.isArray(mergedData) &&(
                      <SmaPreview tickerData={filledHoldings}
                                  etfData={combinedEtfData}
                                  etfColors={etfColors}
                                  sectorColors={sectorColors}
                                  tickersDropped={[...mergedData.filter(x=>!filledHoldings.map(x=>x.symbol).includes(x.symbol))]}
                                  setView={setView}

                      />
            )}
            <Layout.Content>
                {
                    view === 'search' && (
                        <TickerSearch
                              maxTickers={5}
                              types={['etf']}
                              onTickersSubmit={(tickers)=>onAddEtfs(tickers.map(x=>x.symbol))}
                        />
                    )
                }
                {
                    view === 'templates' && (
                        <SmaTemplates onSubmit={()=>setView('table')} onCreate={()=>setView('search')}/>
                    )
                }
                {
                    view === 'drafts' && (
                        <SmaDrafts onSubmit={()=>setView('table')} />
                    )
                }
                {
                    view === 'table' && (
                        mergedData ?
                            <Tabs defaultActiveKey={'2'} tabPosition={'right'}>
                                <Tabs.TabPane key={'1'} tab={'Preview'}>
                                    <SmaPreview tickerData={filledHoldings}
                                                etfData={combinedEtfData}
                                                etfColors={etfColors}
                                                sectorColors={sectorColors}
                                                tickersDropped={[...mergedData.filter(x=>!filledHoldings.map(x=>x.symbol).includes(x.symbol))]}
                                                setView={setView}
                                                isMain={false}

                                    />
                                </Tabs.TabPane>
                                <Tabs.TabPane key={'2'} tab={'Editor'}>
                                    <SmaConfigurator etfData={combinedEtfData}
                                                  tickerData={filledHoldings}
                                                  fullData={mergedData}
                                                  etfColors={etfColors}
                                                  sectorColors={sectorColors}
                                                  config={currentSmaConfig}
                                                  blacklists={blacklists}
                                                  screens={screens}
                                                  summaryDetails={{...summaryDetails, sectorRemainders, sectorWeightChanges}}
                                                  callbacks={{
                                                      removeEtf, toggleReplicateEtf,
                                                      updateConfig, updateEtfWeight,
                                                      saveSma
                                                  }}
                                                  etfContributions={etfContributions}



                                    />
                                </Tabs.TabPane>
                                {cumulativeTableRows && (
                                    <Tabs.TabPane key={'3'} tab={'ETF Table'}>
                                        <div className={'p-6'}>
                                            <CumulativeHoldingsTable detailedRows={cumulativeTableRows} />
                                        </div>
                                    </Tabs.TabPane>
                                )}
                                <Tabs.TabPane key={'4'} tab={'Charts'}>
                                    <SectorComparisonBarChart sectorsBarChartField={sectorsBarChartField}
                                                              setSectorsBarChartField={setSectorsBarChartField}
                                                              sectorsBarData={sectorsBarData}
                                                              summaryDetails={summaryDetails}
                                                              cutTickerData={filledHoldings}
                                                              fullTickerData={mergedData}
                                                              key={replicateKey}
                                    />
                                </Tabs.TabPane>
                                <Tabs.TabPane key={'5'} tab={'Coverage'}>
                                    <EtfCircleCharts condensedTickers={filledHoldings}
                                                     fullTickers={mergedData}
                                                     etfData={Object.values(etfData)}
                                    />
                                </Tabs.TabPane>
                                <Tabs.TabPane key={'11'} tab={'Cube Grid'}>
                                    <div className={'w-full'} style={{height: '100vh'}}>
                                        <StackedCubesChart items={filledHoldings.map(x=>({
                                        label: x.symbol,
                                        value: x.wgt*100,
                                        color: '#000000',
                                        imgUrl: tickers.find(t=>t.symbol===x.symbol)?.logo
                                    }))} />
                                    </div>

                                </Tabs.TabPane>
                                {!isPublic && (
                                    <Tabs.TabPane key={'6'} tab={'Sector Tracking'}>
                                        <SectorTracking allSectors={allSectors} getSectorTickers={getSectorTickers}/>
                                    </Tabs.TabPane>
                                )}
                            </Tabs>
                            :
                            <Placeholder imgUrl={'https://storage.googleapis.com/finiat-web-static-resources/appIcons/cube-grid.jpg'}
                                         text={"No SMA Currently In Progress"}
                                         action={
                                            <Button variant={'contained'} onClick={()=>setView('templates')}>
                                                Start Using an SMA Now
                                            </Button>
                                        }
                            />
                    )
                }
            </Layout.Content>

        </Layout>
    )
}

export default Sma;